import React, { useId, useMemo, useState } from 'react';
import { CalendarIcon, ChevronDownIcon } from '../../../assets/icons/icons';
import { CalendarMonth } from './CalendarMonth';
import { Popover } from './Popover';
import { usePopover } from './usePopover';
import {
    addMonths,
    formatRuDate,
    fromIsoDate,
    startOfDay,
    startOfMonth,
    toIsoDate,
    type IsoDate,
} from './dateUtils';
import styles from './DatePicker.module.scss';

export interface DatePickerProps {
    value: IsoDate | '';
    onChange: (value: IsoDate | '') => void;
    placeholder?: string;
    disabled?: boolean;
    minDate?: Date | null;
    maxDate?: Date | null;
    clearable?: boolean;
    'aria-label'?: string;
}

export const DatePicker = ({
    value,
    onChange,
    placeholder = 'Выберите дату',
    disabled = false,
    minDate = null,
    maxDate = null,
    clearable = true,
    'aria-label': ariaLabel,
}: DatePickerProps) => {
    const { isOpen, setIsOpen, rootRef, popoverRef, position } = usePopover<HTMLDivElement>();
    const popoverId = useId();

    const today = useMemo(() => startOfDay(new Date()), []);
    const selected = value ? fromIsoDate(value) : null;

    const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(selected ?? new Date()));

    const min = minDate ? startOfDay(minDate) : null;
    const max = maxDate ? startOfDay(maxDate) : null;

    const openCalendar = () => {
        setViewMonth(startOfMonth(selected ?? min ?? new Date()));
        setIsOpen(true);
    };

    const handleDayClick = (day: Date) => {
        onChange(toIsoDate(day));
        setIsOpen(false);
    };

    const canGoBack = !min || startOfMonth(viewMonth) > startOfMonth(min);
    const canGoForward = !max || startOfMonth(viewMonth) < startOfMonth(max);

    return (
        <div className={styles.picker} ref={rootRef}>
            <button
                type="button"
                className={styles.trigger}
                onClick={() => (isOpen ? setIsOpen(false) : openCalendar())}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={isOpen ? popoverId : undefined}
                aria-label={ariaLabel}
            >
                <span className={styles.triggerLeading}>
                    <CalendarIcon className={styles.icon} />
                </span>
                <span className={value ? styles.triggerValue : styles.triggerPlaceholder}>
                    {value ? formatRuDate(value) : placeholder}
                </span>
                <span className={styles.triggerTrailing}>
                    <ChevronDownIcon className={styles.icon} />
                </span>
            </button>

            {isOpen && (
                <Popover id={popoverId} label="Выбор даты" popoverRef={popoverRef} position={position}>
                    <div className={styles.months}>
                        <CalendarMonth
                            month={viewMonth}
                            today={today}
                            rangeFrom={selected}
                            rangeTo={selected}
                            isDayDisabled={(day) => Boolean(min && day < min) || Boolean(max && day > max)}
                            onDayClick={handleDayClick}
                            onPrev={() => setViewMonth(addMonths(viewMonth, -1))}
                            onNext={() => setViewMonth(addMonths(viewMonth, 1))}
                            canPrev={canGoBack}
                            canNext={canGoForward}
                        />
                    </div>

                    {clearable && (
                        <div className={styles.popoverFooter}>
                            <span className={styles.hint}>{value ? formatRuDate(value) : placeholder}</span>
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                    onChange('');
                                    setIsOpen(false);
                                }}
                                disabled={!value}
                            >
                                Сбросить
                            </button>
                        </div>
                    )}
                </Popover>
            )}
        </div>
    );
};